/**
 * InventoryTurnoverScreen — how many times inventory turned over in the window,
 * overall and by category, with days of inventory on hand.
 *
 * Reuses the Dead Stock loader (products + net sales over the window) and values
 * turnover at the current purchase price, so it needs no `cost_price` deploy.
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { toDateInputValue } from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadDeadStock } from '@/reports/deadStock/deadStockDataSource';
import { buildInventoryTurnover, type InventoryTurnover } from '@/reports/turnover/inventoryTurnover';

const WINDOWS = [30, 90, 180] as const;
const turnsStr = (n: number | null) => (n == null ? '—' : `${n.toFixed(2)}×`);
const daysStr = (n: number | null) => (n == null ? '—' : `${n} d`);

// ─── Component ───────────────────────────────────────────────────────────────

export function InventoryTurnoverScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [windowDays, setWindowDays] = useState<number>(90);
  const [data, setData]       = useState<InventoryTurnover | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const source = await loadDeadStock(activeStoreId, windowDays, 180, new Date());
      setData(buildInventoryTurnover({
        windowDays,
        products: source.products.map((p) => ({
          id: p.id, name: p.name, category: p.category,
          currentStock: p.currentStock, purchasePrice: p.purchasePrice, unitsSold: p.unitsSold,
        })),
      }));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, windowDays]);

  useEffect(() => { void load(); }, [load]);

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = toCsv(data.products, [
        { header: 'Product', value: (r) => r.name },
        { header: 'Category', value: (r) => r.category },
        { header: 'Stock', value: (r) => r.currentStock },
        { header: 'Units sold', value: (r) => r.unitsSold },
        { header: 'Inventory value', value: (r) => r.inventoryValue },
        { header: 'COGS', value: (r) => r.cogs },
        { header: 'Turns', value: (r) => (r.turns ?? '') },
        { header: 'Days of inventory', value: (r) => (r.daysOfInventory ?? '') },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `InventoryTurnover_${windowDays}d_${toDateInputValue(new Date())}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      await pdf.saveFromHtml({
        html: buildTurnoverHtml(data),
        defaultFileName: `InventoryTurnover_${windowDays}d_${toDateInputValue(new Date())}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body text-fg-tertiary">Window:</span>
        {WINDOWS.map((w) => (
          <button key={w} type="button" onClick={() => setWindowDays(w)}
            className={cn('rounded-md px-3 py-1.5 text-body transition-colors',
              windowDays === w ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')}>
            {w} days
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.products.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting || data.products.length === 0}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : data.products.length === 0 ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">No stocked products to analyse.</div>
      ) : (
        <>
          {/* Overall KPIs */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Turnover" value={turnsStr(data.overall.turns)} accent />
            <KPI label="Days of inventory" value={daysStr(data.overall.daysOfInventory)} />
            <KPI label="Inventory value" value={formatCurrency(data.overall.inventoryValue)} />
            <KPI label={`COGS (${data.windowDays}d)`} value={formatCurrency(data.overall.cogs)} />
          </div>

          {/* By category */}
          {data.byCategory.length > 0 && (
            <Card title="Turnover by category">
              <table className="w-full text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="pb-1 text-left font-normal">Category</th>
                    <th className="pb-1 text-right font-normal">Inventory</th>
                    <th className="pb-1 text-right font-normal">COGS</th>
                    <th className="pb-1 text-right font-normal">Turns</th>
                    <th className="pb-1 text-right font-normal">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byCategory.map((c) => (
                    <tr key={c.category} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5 text-fg-primary">{c.category}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(c.inventoryValue)}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{formatCurrency(c.cogs)}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-primary">{turnsStr(c.turns)}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{daysStr(c.daysOfInventory)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* Products, fastest first */}
          <Card title="Turnover by product (fastest first)">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="pb-1 text-left font-normal">Product</th>
                    <th className="pb-1 text-right font-normal">Stock</th>
                    <th className="pb-1 text-right font-normal">Sold</th>
                    <th className="pb-1 text-right font-normal">Inventory</th>
                    <th className="pb-1 text-right font-normal">Turns</th>
                    <th className="pb-1 text-right font-normal">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {data.products.map((r) => (
                    <tr key={r.productId} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5">
                        <div className="text-fg-primary">{r.name}</div>
                        <div className="text-label text-fg-tertiary">{r.category}</div>
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-fg-secondary">{r.currentStock}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{r.unitsSold}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(r.inventoryValue)}</td>
                      <td className={cn('py-1.5 text-right font-medium tabular-nums', r.turns != null && r.turns < 1 ? 'text-warning-ink' : 'text-fg-primary')}>{turnsStr(r.turns)}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{daysStr(r.daysOfInventory)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function KPI({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', accent ? 'text-accent-ink' : 'text-fg-primary')}>{value}</div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildTurnoverHtml(d: InventoryTurnover): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);
  const turns = (n: number | null) => (n == null ? '—' : `${n.toFixed(2)}×`);
  const days = (n: number | null) => (n == null ? '—' : `${n} d`);

  const catRows = d.byCategory.map((c) =>
    `<tr><td>${esc(c.category)}</td><td style="text-align:right">${fmt(c.inventoryValue)}</td><td style="text-align:right">${fmt(c.cogs)}</td><td style="text-align:right">${turns(c.turns)}</td><td style="text-align:right">${days(c.daysOfInventory)}</td></tr>`,
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}h2{font-size:13px;margin-top:22px;text-transform:uppercase;color:#666}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px}th,td{padding:5px 8px;border-bottom:1px solid #eee}
th{text-align:right;color:#666;font-weight:500}th:first-child,td:first-child{text-align:left}</style></head>
<body>
<h1>Inventory Turnover</h1>
<p style="color:#888">Window ${d.windowDays} days · Overall ${turns(d.overall.turns)} · Days of inventory ${days(d.overall.daysOfInventory)} · Inventory ${fmt(d.overall.inventoryValue)} · COGS ${fmt(d.overall.cogs)}</p>
<h2>By category</h2>
<table><thead><tr><th>Category</th><th>Inventory</th><th>COGS</th><th>Turns</th><th>Days</th></tr></thead><tbody>${catRows}</tbody></table>
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}. Valued at current purchase price.</p>
</body></html>`;
}
